'use client'

import { RIIPFormDialog } from '@/components/forms/riip-form-dialog'
import {
  PremiumFeature,
  PremiumHeading,
  PremiumHero,
  PremiumIntro,
  PremiumSection,
} from '@/components/premium/premium-ui'
import { Button } from '@/components/ui/button'
import { BookOpen, GraduationCap, Heart, MapPin, Medal, MessageCircle, PenTool, ShieldCheck, Sparkles, Target, Users } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import Image from 'next/image'
import { toast } from 'sonner'

export const RIIPPage = () => {
  const handleSuccess = () => {
    toast.success('Application submitted', { description: 'We will contact you soon with more details.' })
  }
  const handleError = () => {
    toast.error('Submission failed', { description: 'Please try again or contact support.' })
  }

  return (
    <div className="bg-background">
      <PremiumHero
        image="/riip-image.jpeg"
        imageAlt="Students documenting rural stories during the Oorva Rural Immersive Programme"
        eyebrow="RIIP for Students"
        title={
          <>
            Rural Impact
            <br />
            Immersive Programme
            <br />
            <span className="italic text-accent-foreground">for students.</span>
          </>
        }
        subtitle="A hands-on cultural learning journey where students meet local communities, document real stories and turn field experiences into meaningful impact."
      >
        <RIIPFormDialog
          trigger={<Button className="min-h-12 rounded-full bg-brand px-7 text-white hover:bg-brand-hover">Enquire about RIIP</Button>}
          onSuccess={handleSuccess}
          onError={handleError}
        />
      </PremiumHero>

      <PremiumSection>
        <div className="grid gap-8 rounded-[1.5rem] border border-border bg-card p-6 md:grid-cols-[0.9fr_1.1fr] md:p-10">
          <div>
            <PremiumHeading className="text-3xl md:text-5xl">What is RIIP for Students?</PremiumHeading>
            <PremiumIntro className="mt-5">
              RIIP takes students out of the classroom and into workshops, farms and living
              cultural spaces where they observe, document and solve real rural challenges.
            </PremiumIntro>
            <div className="mt-8 grid grid-cols-2 gap-4">
              <PremiumFeature icon={<GraduationCap className="h-5 w-5" />} title="Experiential learning" body="Students learn by doing, seeing and asking better questions." />
              <PremiumFeature icon={<Heart className="h-5 w-5" />} title="Real impact" body="Each visit supports communities and local knowledge systems." />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="relative min-h-[340px] overflow-hidden rounded-2xl sm:row-span-2">
              <Image src="/weekend-trips/silk-weaving-weaver.webp" alt="Students visiting a rural host" fill className="object-cover" sizes="(min-width: 768px) 28vw, 100vw" />
            </div>
            <div className="relative min-h-[160px] overflow-hidden rounded-2xl">
              <Image src="/weekend-trips/doll-making-painting.png" alt="Hands-on cultural learning" fill className="object-cover" sizes="(min-width: 768px) 28vw, 100vw" />
            </div>
            <div className="relative min-h-[160px] overflow-hidden rounded-2xl">
              <Image src="/sirumugai-story.jpeg" alt="Student field documentation" fill className="object-cover" sizes="(min-width: 768px) 28vw, 100vw" />
            </div>
          </div>
        </div>
      </PremiumSection>

      <PremiumSection>
        <div className="mx-auto max-w-3xl text-center">
          <PremiumHeading className="text-3xl md:text-5xl">What students actually do</PremiumHeading>
        </div>
        <div className="mt-10 grid gap-5 md:grid-cols-3 lg:grid-cols-6">
          {([
            ['Prepare', BookOpen, 'Understand the host, craft and place before the visit.'],
            ['Meet the host', MessageCircle, 'Build a meaningful connection before the activity begins.'],
            ['Map the journey', MapPin, 'Document routes, people, processes and observations.'],
            ['Create', PenTool, 'Make, taste, try, observe and reflect.'],
            ['Tell the story', Sparkles, 'Convert learning into a clear output or presentation.'],
            ['Earn recognition', Medal, 'Receive certification for completing the journey.'],
          ] as Array<[string, LucideIcon, string]>).map(([title, Icon, body]) => (
            <PremiumFeature key={title} icon={<Icon className="h-5 w-5" />} title={title} body={body} />
          ))}
        </div>
      </PremiumSection>

      <PremiumSection className="pt-0">
        <div>
          <PremiumHeading className="text-center text-3xl md:text-5xl">Why institutions choose RIIP for Students</PremiumHeading>
          <div className="mt-10 grid gap-5 md:grid-cols-5">
            <PremiumFeature icon={<BookOpen className="h-5 w-5" />} title="Enrich learning" body="Bring textbook topics to life through real experiences." />
            <PremiumFeature icon={<Users className="h-5 w-5" />} title="Build values" body="Encourage respect for traditions and community pride." />
            <PremiumFeature icon={<Target className="h-5 w-5" />} title="Curriculum aligned" body="Support experiential learning and multiple curricular goals." />
            <PremiumFeature icon={<ShieldCheck className="h-5 w-5" />} title="Safe & structured" body="Clear coordination, hosts and supervision." />
            <PremiumFeature icon={<Sparkles className="h-5 w-5" />} title="Memorable outputs" body="Students return with stories, artefacts and reflection." />
          </div>
        </div>
      </PremiumSection>

      <PremiumSection className="pt-0">
        <div className="rounded-[1.5rem] border border-border bg-card p-6 md:p-10">
          <PremiumHeading className="text-3xl md:text-4xl">Plan an immersion</PremiumHeading>
          <div className="mt-6 grid gap-5 md:grid-cols-2">
            <p className="text-sm leading-7 text-muted-foreground"><strong className="text-foreground">Who should enquire.</strong> Teachers, school leaders and college teams can share their institution, student group size and learning goals.</p>
            <p className="text-sm leading-7 text-muted-foreground"><strong className="text-foreground">Sample journey.</strong> Prepare a question in class, visit local hosts to observe and document, then turn the findings into a project or presentation. Duration and activities are tailored to the group.</p>
            <p className="text-sm leading-7 text-muted-foreground"><strong className="text-foreground">Before the field visit.</strong> We will agree the destination, transport, accessibility needs and staff supervision responsibilities with your institution.</p>
            <p className="text-sm leading-7 text-muted-foreground"><strong className="text-foreground">Cost and next steps.</strong> Enquire for a proposed plan and quote. Dates, group size, price and response timing are confirmed with your team before you commit.</p>
          </div>
        </div>
      </PremiumSection>

      <PremiumSection tone="forest">
        <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
          <div>
            <PremiumHeading className="text-white">
              Bring RIIP for Students to
              <br />
              <span className="italic text-brand-highlight">your institution.</span>
            </PremiumHeading>
            <p className="mt-5 max-w-md text-white/68">
              Let your students explore, experience and express. We will help design the right
              programme for your institution.
            </p>
          </div>
          <div className="rounded-3xl border border-white/10 bg-white/[0.055] p-8">
            <h3 className="font-sans text-4xl font-semibold tracking-[-0.035em] text-white">We would love to connect.</h3>
            <p className="mt-3 text-white/62">Share your details and our team will reach out with next steps.</p>
            <div className="mt-7">
              <RIIPFormDialog
                trigger={<Button className="min-h-12 rounded-full bg-brand px-7 text-white hover:bg-brand-hover">Talk to our team</Button>}
                onSuccess={handleSuccess}
                onError={handleError}
              />
            </div>
          </div>
        </div>
      </PremiumSection>
    </div>
  )
}
